import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { Property } from "@triple-h/shared";
import { propertyDisplayTitle, contentExcerpt } from "./site";

const CANONICAL_ORIGIN = "https://triplehmyanmar.com";
const DEFAULT_IMAGE = "/brand/logo.png";

type MetaOptions = {
  title: string;
  description?: string;
  image?: string | null;
  type?: string;
  noindex?: boolean;
};

function setMeta(selector: string, attribute: "content" | "href", value: string) {
  const element = document.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
  if (element) element.setAttribute(attribute, value);
}

export function useDocumentMeta({ title, description, image, type = "website", noindex = false }: MetaOptions) {
  const location = useLocation();

  useEffect(() => {
    const descriptionTag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const canonicalTag = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    const ogDescription = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
    const ogImage = document.querySelector<HTMLMetaElement>('meta[property="og:image"]');
    const ogType = document.querySelector<HTMLMetaElement>('meta[property="og:type"]');
    const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    const twitterTitle = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]');
    const twitterImage = document.querySelector<HTMLMetaElement>('meta[name="twitter:image"]');
    const robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const previous = {
      title: document.title,
      description: descriptionTag?.content,
      canonical: canonicalTag?.href,
      ogTitle: ogTitle?.content,
      ogDescription: ogDescription?.content,
      ogImage: ogImage?.content,
      ogType: ogType?.content,
      ogUrl: ogUrl?.content,
      twitterTitle: twitterTitle?.content,
      twitterDescription: twitterDescription?.content,
      twitterImage: twitterImage?.content,
      robots: robots?.content
    };

    const canonical = `${CANONICAL_ORIGIN}${location.pathname || "/"}`;
    const imageUrl = image ? new URL(image, CANONICAL_ORIGIN).toString() : `${CANONICAL_ORIGIN}${DEFAULT_IMAGE}`;
    const desc = description || "Browse genuine property listings managed by Triple H Myanmar.";

    document.title = title;
    setMeta('meta[name="description"]', "content", desc);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:image"]', "content", imageUrl);
    setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]', "content", imageUrl);

    if (robots) robots.content = noindex ? "noindex, nofollow" : "index, follow";

    return () => {
      document.title = previous.title;
      if (descriptionTag && previous.description !== undefined) descriptionTag.content = previous.description;
      if (canonicalTag && previous.canonical) canonicalTag.href = previous.canonical;
      if (ogTitle && previous.ogTitle !== undefined) ogTitle.content = previous.ogTitle;
      if (ogDescription && previous.ogDescription !== undefined) ogDescription.content = previous.ogDescription;
      if (ogImage && previous.ogImage !== undefined) ogImage.content = previous.ogImage;
      if (ogType && previous.ogType !== undefined) ogType.content = previous.ogType;
      if (ogUrl && previous.ogUrl !== undefined) ogUrl.content = previous.ogUrl;
      if (twitterTitle && previous.twitterTitle !== undefined) twitterTitle.content = previous.twitterTitle;
      if (twitterDescription && previous.twitterDescription !== undefined) twitterDescription.content = previous.twitterDescription;
      if (twitterImage && previous.twitterImage !== undefined) twitterImage.content = previous.twitterImage;
      if (robots && previous.robots !== undefined) robots.content = previous.robots;
    };
  }, [title, description, image, type, noindex, location.pathname]);
}

export function propertyRef(slug: string) {
  return (slug.startsWith("listing-") ? slug.slice(8) : slug).slice(0, 10);
}

function cutGraphemes(text: string, limit: number) {
  let result = "";
  for (const item of new Intl.Segmenter("my", { granularity: "grapheme" }).segment(text.trim())) {
    if ((result + item.segment).length > limit) break;
    result += item.segment;
  }
  return result.trim();
}

export function propertySeoTitle(property: Property) {
  return `${cutGraphemes(propertyDisplayTitle(property) || "Property in Myanmar", 34)} | Triple H · ${propertyRef(property.slug)}`;
}

export function propertySeoDescription(property: Property) {
  const text = contentExcerpt(property);
  const topic = text.length > 55
    ? text
    : `View images and details for ${property.title} in ${property.locationText || "Myanmar"} on Triple H Myanmar.`;
  return `${cutGraphemes(topic, 128)} · Ref ${propertyRef(property.slug)}`;
}
